import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';

export type ViewMode = 'monitor' | 'desktop' | 'mobile';

interface ViewModeConfig {
  mode: ViewMode;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isMonitor: boolean;
  screenWidth: number;
  screenHeight: number;
}

export const useViewMode = (): ViewModeConfig => {
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Determine view mode based on screen size and user role
    const determineViewMode = (): ViewMode => {
      // Mobile device detection
      if (screenWidth <= 768) {
        return 'mobile';
      }
      
      // Large screen monitor mode detection (usually widescreen above 1920px)
      if (screenWidth >= 1920 && screenHeight >= 1080) {
        // Admin role prioritizes monitor large screen mode
        if (user?.role === 'admin') {
          return 'monitor';
        }
      }
      
      // Preference settings based on user role
      if (user?.role === 'ceo' && screenWidth <= 768) {
        return 'mobile';
      }
      
      if (user?.role === 'sales' && screenWidth >= 1024) {
        return 'desktop';
      }
      
      // Default desktop mode
      return 'desktop';
    };

    setViewMode(determineViewMode());
  }, [screenWidth, screenHeight, user?.role]);

  return {
    mode: viewMode,
    isMobile: viewMode === 'mobile',
    isTablet: screenWidth > 768 && screenWidth <= 1024,
    isDesktop: viewMode === 'desktop',
    isMonitor: viewMode === 'monitor',
    screenWidth,
    screenHeight,
  };
};

export default useViewMode;